import React, { useState, useEffect, useCallback } from 'react';
import { fundApi } from '../../services/api';
import { FundSummary, FundTransaction } from '../../types/fund.types';
import { fmt, formatCurrency } from '../../utils/formatters';
import './FundManagement.css';

const FundManagement: React.FC = () => {
  const [summary, setSummary] = useState<FundSummary | null>(null);
  const [transactions, setTransactions] = useState<FundTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [txType, setTxType] = useState<'DEPOSIT' | 'WITHDRAWAL'>('DEPOSIT');
  const [investorName, setInvestorName] = useState('');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [summaryData, txData] = await Promise.all([
        fundApi.getSummary(),
        fundApi.getTransactions(),
      ]);
      setSummary(summaryData);
      setTransactions(txData);
    } catch (error) {
      console.error('[FundManagement] Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    const parsedAmount = parseFloat(amount);
    if (!investorName || !parsedAmount || parsedAmount <= 0) {
      setFormError('Please fill in all required fields with valid values.');
      return;
    }

    setSubmitting(true);
    try {
      const request = { investorName, amount: parsedAmount, notes: notes || undefined };
      if (txType === 'DEPOSIT') {
        await fundApi.deposit(request);
      } else {
        await fundApi.withdraw(request);
      }
      setFormSuccess(`${txType === 'DEPOSIT' ? 'Deposit' : 'Withdrawal'} of $${fmt(parsedAmount)} recorded successfully.`);
      setAmount('');
      setNotes('');
      await fetchData();
    } catch (error: any) {
      const msg = error?.response?.data?.error || 'Failed to record transaction.';
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Get unique investor names from transactions for the dropdown
  const investorNames = React.useMemo(() => {
    if (!summary) return [];
    return summary.investors.map(inv => inv.name);
  }, [summary]);

  if (loading) {
    return (
      <div className="fund-page">
        <div className="page-header">
          <h2>Fund Management</h2>
          <p className="page-description">NAV-based fund tracking, deposits and withdrawals</p>
        </div>
        <div className="fund-loading">Loading fund data...</div>
      </div>
    );
  }

  return (
    <div className="fund-page">
      <div className="page-header">
        <h2>Fund Management</h2>
        <p className="page-description">NAV-based fund tracking, deposits and withdrawals</p>
      </div>

      {/* Fund Summary */}
      {summary && (
        <div className="fund-card">
          <h3 className="fund-section-title">Fund Summary</h3>
          <div className="fund-summary-grid">
            <div className="fund-metric">
              <span className="fund-metric-label">Total Invested</span>
              <span className="fund-metric-value">${fmt(summary.totalInvestedCapital, 0)}</span>
            </div>
            <div className="fund-metric">
              <span className="fund-metric-label">NAV per Unit</span>
              <span className={`fund-metric-value ${summary.navPerUnit >= 1 ? 'positive' : 'negative'}`}>
                ${summary.navPerUnit.toFixed(6)}
              </span>
            </div>
            <div className="fund-metric">
              <span className="fund-metric-label">Total Fund Value</span>
              <span className={`fund-metric-value ${summary.totalFundValue >= summary.totalInvestedCapital ? 'positive' : 'negative'}`}>
                ${fmt(summary.totalFundValue)}
              </span>
            </div>
            <div className="fund-metric">
              <span className="fund-metric-label">Cumulative P/L</span>
              <span className={`fund-metric-value ${summary.cumulativePL >= 0 ? 'positive' : 'negative'}`}>
                {summary.cumulativePL >= 0 ? '+' : ''}{formatCurrency(summary.cumulativePL)}
              </span>
            </div>
            <div className="fund-metric">
              <span className="fund-metric-label">Total Units</span>
              <span className="fund-metric-value">{fmt(summary.totalOutstandingUnits, 0)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Investors */}
      {summary && summary.investors.length > 0 && (
        <div className="fund-card">
          <h3 className="fund-section-title">Investors</h3>
          <div className="fund-table-wrapper">
            <table className="fund-table">
              <thead>
                <tr>
                  <th className="text-left">Name</th>
                  <th className="text-right">Ownership</th>
                  <th className="text-right">Units</th>
                  <th className="text-right">Invested</th>
                  <th className="text-right">Value</th>
                  <th className="text-right">P/L</th>
                  <th className="text-right">Return</th>
                </tr>
              </thead>
              <tbody>
                {summary.investors.map((inv) => (
                  <tr key={inv.name}>
                    <td className="text-left font-semibold">{inv.name}</td>
                    <td className="text-right">{fmt(inv.ownershipPct, 1)}%</td>
                    <td className="text-right">{fmt(inv.unitsOwned, 0)}</td>
                    <td className="text-right">${fmt(inv.netInvested, 0)}</td>
                    <td className={`text-right ${inv.currentValue >= inv.netInvested ? 'positive' : 'negative'}`}>
                      ${fmt(inv.currentValue)}
                    </td>
                    <td className={`text-right font-bold ${inv.profitLoss >= 0 ? 'positive' : 'negative'}`}>
                      {inv.profitLoss >= 0 ? '+' : ''}{formatCurrency(inv.profitLoss)}
                    </td>
                    <td className={`text-right ${inv.returnPct >= 0 ? 'positive' : 'negative'}`}>
                      {inv.returnPct >= 0 ? '+' : ''}{fmt(inv.returnPct, 2)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Record Transaction */}
      <div className="fund-card">
        <h3 className="fund-section-title">Record Transaction</h3>
        <form className="fund-form" onSubmit={handleSubmit}>
          <div className="fund-form-row">
            <div className="fund-toggle-group">
              <button
                type="button"
                className={`fund-toggle-btn ${txType === 'DEPOSIT' ? 'active deposit' : ''}`}
                onClick={() => setTxType('DEPOSIT')}
              >
                Deposit
              </button>
              <button
                type="button"
                className={`fund-toggle-btn ${txType === 'WITHDRAWAL' ? 'active withdrawal' : ''}`}
                onClick={() => setTxType('WITHDRAWAL')}
              >
                Withdrawal
              </button>
            </div>
          </div>

          <div className="fund-form-row">
            <label className="fund-label">Investor</label>
            <select
              className="fund-select"
              value={investorName}
              onChange={(e) => setInvestorName(e.target.value)}
            >
              <option value="">Select Investor</option>
              {investorNames.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>

          <div className="fund-form-row">
            <label className="fund-label">Amount ($)</label>
            <input
              type="number"
              className="fund-input"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              min="0.01"
              step="0.01"
            />
          </div>

          <div className="fund-form-row">
            <label className="fund-label">Notes (optional)</label>
            <input
              type="text"
              className="fund-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Additional capital injection"
            />
          </div>

          {formError && <div className="fund-form-error">{formError}</div>}
          {formSuccess && <div className="fund-form-success">{formSuccess}</div>}

          <button
            type="submit"
            className={`fund-submit-btn ${txType === 'DEPOSIT' ? 'deposit' : 'withdrawal'}`}
            disabled={submitting}
          >
            {submitting ? 'Processing...' : `Record ${txType === 'DEPOSIT' ? 'Deposit' : 'Withdrawal'}`}
          </button>
        </form>
      </div>

      {/* Transaction History */}
      <div className="fund-card">
        <h3 className="fund-section-title">Transaction History</h3>
        {transactions.length === 0 ? (
          <div className="fund-empty">No transactions recorded yet.</div>
        ) : (
          <div className="fund-table-wrapper">
            <table className="fund-table">
              <thead>
                <tr>
                  <th className="text-left">Date</th>
                  <th className="text-left">Investor</th>
                  <th className="text-left">Type</th>
                  <th className="text-right">Amount</th>
                  <th className="text-right">Units</th>
                  <th className="text-right">NAV</th>
                  <th className="text-left">Notes</th>
                </tr>
              </thead>
              <tbody>
                {[...transactions].reverse().map((tx) => (
                  <tr key={tx.id}>
                    <td className="text-left nowrap">
                      {new Date(tx.transactionDate).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric'
                      })}
                    </td>
                    <td className="text-left">{tx.investorName}</td>
                    <td className="text-left">
                      <span className={`tx-type-badge ${tx.transactionType.toLowerCase()}`}>
                        {tx.transactionType}
                      </span>
                    </td>
                    <td className="text-right">${fmt(tx.amount)}</td>
                    <td className="text-right">{fmt(tx.unitsTransacted, 2)}</td>
                    <td className="text-right">${tx.navAtTransaction.toFixed(6)}</td>
                    <td className="text-left fund-notes">{tx.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default FundManagement;
